import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ToastProvider';
import { phoneMask, cpfMask, unmask } from '../../lib/masks';
import { MetaIntegrationTerms } from '../legal/MetaIntegrationTerms';
import { MetaIntegrationPrivacy } from '../legal/MetaIntegrationPrivacy';
import { ErrorModal } from '../../components/ErrorModal';

interface ConnectedAccount {
  _id: string;
  platform: 'instagram' | 'facebook';
  accountId: string;
  accountName: string;
  accountUsername?: string;
  profilePictureUrl?: string;
  isActive: boolean;
  createdAt: string;
}

export function AccountPage(){
  const { user, token, refresh } = useAuth();
  const [plan,setPlan] = useState('Pro');
  const [loading,setLoading] = useState<string>('');
  const [error,setError] = useState<string| null>(null);

  // Estado para edição de perfil
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [cpf, setCpf] = useState(user?.cpf || '');
  const [birthDate, setBirthDate] = useState(
    user?.birthDate ? new Date(user.birthDate).toISOString().split('T')[0] : ''
  );

  // Estado para seleção de assinatura
  const [selectedCycle, setSelectedCycle] = useState<'MONTHLY'|'YEARLY'>('MONTHLY');
  const [selectedPayment, setSelectedPayment] = useState<'BOLETO'|'CREDIT_CARD'|'PIX'>('PIX');

  // Estado para dados do cartão
  const [cardData, setCardData] = useState({
    holderName: '',
    number: '',
    expiryMonth: '',
    expiryYear: '',
    ccv: ''
  });

  const [cardHolderInfo, setCardHolderInfo] = useState({
    name: user?.name || '',
    email: user?.email || '',
    cpfCnpj: user?.cpf?.replace(/\D/g, '') || '',
    postalCode: '',
    addressNumber: '',
    addressComplement: '',
    phone: user?.phone?.replace(/\D/g, '') || ''
  });

  // Estado para resultado do pagamento
  const [paymentResult, setPaymentResult] = useState<any>(() => {
    const saved = localStorage.getItem('pending_payment');
    return saved ? JSON.parse(saved) : null;
  });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [justCancelled, setJustCancelled] = useState(false);

  // Estados para Integração Meta
  const [advancedModeEnabled, setAdvancedModeEnabled] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [modalView, setModalView] = useState<'intro' | 'terms' | 'privacy'>('intro');
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  // Estado para modal de erro customizado
  const [errorModal, setErrorModal] = useState<{
    isOpen: boolean;
    title?: string;
    message: string;
    code?: string;
    suggestion?: string;
    actionLabel?: string;
    onAction?: () => void;
  }>({
    isOpen: false,
    message: ''
  });

  // Estado para LGPD - Exportação e Exclusão
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const { push } = useToast();

  // Helper para mostrar erro em modal
  const showErrorModal = (
    message: string,
    options?: {
      title?: string;
      code?: string;
      suggestion?: string;
      actionLabel?: string;
      onAction?: () => void;
    }
  ) => {
    setErrorModal({
      isOpen: true,
      message,
      ...options
    });
  };

  const closeErrorModal = () => {
    setErrorModal(prev => ({ ...prev, isOpen: false }));
  };

  // Salva/Remove paymentResult do localStorage quando mudar
  useEffect(() => {
    if (paymentResult && user?.subscriptionStatus === 'Aguardando Pagamento') {
      localStorage.setItem('pending_payment', JSON.stringify(paymentResult));
    } else {
      localStorage.removeItem('pending_payment');
    }
  }, [paymentResult, user?.subscriptionStatus]);

  // Polling para verificar se o pagamento foi confirmado
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    // Se o usuário está com status "Aguardando Pagamento", faz polling
    if (user?.subscriptionStatus === 'Aguardando Pagamento') {
      console.log('[POLLING] Iniciando verificação automática de pagamento...');

      intervalId = setInterval(async () => {
        console.log('[POLLING] Verificando status do pagamento...');
        await refresh();
      }, 10000); // Verifica a cada 10 segundos
    }

    return () => {
      if (intervalId) {
        console.log('[POLLING] Parando verificação automática');
        clearInterval(intervalId);
      }
    };
  }, [user?.subscriptionStatus, refresh]);

  // Detecta quando o pagamento foi confirmado
  useEffect(() => {
    const previousStatus = sessionStorage.getItem('previous_subscription_status');
    const currentStatus = user?.subscriptionStatus;

    // Só mostra toast de confirmação se não acabou de cancelar
    if (previousStatus === 'Aguardando Pagamento' && currentStatus === 'Ativa' && !justCancelled) {
      console.log('[PAYMENT] 🎉 Pagamento confirmado!');
      push('Pagamento confirmado! Seu plano Pro foi ativado com sucesso!', 'success');
      setShowPaymentModal(false); // Fecha a janela se estiver aberta
      setPaymentResult(null); // Limpa resultado de pagamento
      localStorage.removeItem('pending_payment'); // Limpa do localStorage
    }

    if (currentStatus) {
      sessionStorage.setItem('previous_subscription_status', currentStatus);
    }
  }, [user?.subscriptionStatus, push, justCancelled]);

  // Carregar contas conectadas e tratar OAuth callback
  useEffect(() => {
    if (user) {
      loadConnectedAccounts();
    }

    // Tratar query params de retorno do OAuth
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const success = params.get('success');
    const count = params.get('count');

    if (error) {
      const errorMessages: Record<string, string> = {
        'oauth_denied': 'Você negou as permissões. Tente novamente.',
        'invalid_callback': 'Callback inválido. Entre em contato com o suporte.',
        'invalid_state': 'State inválido. Sessão expirada.',
        'state_expired': 'Sessão expirou. Tente conectar novamente.',
        'no_pages': 'Nenhuma página encontrada. Crie uma Página no Facebook primeiro.',
        'connection_failed': 'Erro ao conectar. Tente novamente.'
      };
      push(errorMessages[error] || 'Erro desconhecido', 'error');
      window.history.replaceState({}, '', '/app/account');
    }

    if (success === 'accounts_connected') {
      push(`${count || 0} conta(s) conectada(s) com sucesso!`, 'success');
      window.history.replaceState({}, '', '/app/account');
      loadConnectedAccounts();
    }
  }, [user]);

  const loadConnectedAccounts = async () => {
    if (!token) return;

    try {
      setLoadingAccounts(true);
      const data = await api.listConnectedAccounts(token);
      setConnectedAccounts(data.accounts as ConnectedAccount[]);
      setAdvancedModeEnabled(data.accounts.length > 0);
    } catch (error: any) {
      console.error('Erro ao carregar contas:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleEnableAdvancedMode = () => {
    setShowTermsModal(true);
    setModalView('intro');
    setTermsAccepted(false);
    setPrivacyAccepted(false);
  };

  const handleAcceptTerms = async () => {
    if (!termsAccepted || !privacyAccepted || !token) {
      push('Você deve aceitar ambos os termos', 'error');
      return;
    }

    try {
      setLoadingMeta(true);
      await api.enableAdvancedMode(termsAccepted, privacyAccepted, token);
      setAdvancedModeEnabled(true);
      setShowTermsModal(false);
      push('Modo avançado ativado! Conecte suas contas agora.', 'success');
    } catch (error: any) {
      push(error.message || 'Erro ao ativar modo avançado', 'error');
    } finally {
      setLoadingMeta(false);
    }
  };

  const handleDisableAdvancedMode = async () => {
    if (!confirm('Desativar o modo avançado irá desconectar todas as suas contas Meta. Deseja continuar?')) {
      return;
    }

    if (!token) return;

    try {
      setLoadingMeta(true);
      await api.disableAdvancedMode(token);
      setAdvancedModeEnabled(false);
      setConnectedAccounts([]);
      push('Modo avançado desativado', 'success');
    } catch (error: any) {
      push(error.message || 'Erro ao desativar modo avançado', 'error');
    } finally {
      setLoadingMeta(false);
    }
  };

  const handleConnectFacebook = async () => {
    if (!token) return;

    try {
      const data = await api.initiateOAuth(token);
      window.location.href = data.authUrl;
    } catch (error: any) {
      push(error.message || 'Erro ao iniciar conexão', 'error');
    }
  };

  const handleDisconnect = async (accountId: string, accountName: string) => {
    if (!confirm(`Desconectar ${accountName}?`)) return;
    if (!token) return;

    try {
      await api.disconnectAccount(accountId, token);
      push('Conta desconectada', 'success');
      loadConnectedAccounts();
    } catch (error: any) {
      push(error.message || 'Erro ao desconectar', 'error');
    }
  };

  async function wrap(name:string, fn:()=>Promise<any>){
    if(!token) return; setError(null); setLoading(name);
    try {
      const result = await fn();
      if (name !== 'cancelar') {
        await refresh();
      }
      return result;
    } catch(e:any){
      setError(e.message);
      throw e;
    } finally {
      setLoading('');
    }
  }

  async function handleUpdateProfile(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;

    setLoading('update');
    setError(null);

    try {
      await api.updateProfile({
        name,
        phone: phone ? unmask(phone) : undefined,
        cpf: cpf ? unmask(cpf) : undefined,
        birthDate: birthDate || undefined
      }, token);

      await refresh();
      setEditing(false);
      push('Perfil atualizado com sucesso!', 'success');
    } catch (e: any) {
      setError(e.message);
      push(e.message, 'error');
    } finally {
      setLoading('');
    }
  }

  function cancelEdit() {
    setEditing(false);
    setName(user?.name || '');
    setPhone(user?.phone || '');
    setCpf(user?.cpf || '');
    setBirthDate(user?.birthDate ? new Date(user.birthDate).toISOString().split('T')[0] : '');
    setError(null);
  }

  // Exportar dados do usuário (LGPD)
  async function handleExportData() {
    if (!token) return;

    setLoadingExport(true);
    try {
      const data = await api.exportUserData(token);

      // Criar e baixar arquivo JSON
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `copygenius-meus-dados-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      push('Seus dados foram exportados com sucesso!', 'success');
    } catch (e: any) {
      push('Erro ao exportar dados: ' + e.message, 'error');
    } finally {
      setLoadingExport(false);
    }
  }

  // Deletar conta do usuário (LGPD)
  async function handleDeleteAccount() {
    if (!token) return;

    if (deleteConfirmation !== 'DELETAR MINHA CONTA') {
      push('Digite exatamente "DELETAR MINHA CONTA" para confirmar.', 'error');
      return;
    }

    setLoadingDelete(true);
    try {
      const result = await api.deleteAccount(deleteConfirmation, token);

      push('Sua conta foi deletada permanentemente.', 'success');
      setShowDeleteModal(false);

      // Logout e redirecionar
      localStorage.removeItem('token');
      window.location.href = '/';
    } catch (e: any) {
      push('Erro ao deletar conta: ' + e.message, 'error');
    } finally {
      setLoadingDelete(false);
    }
  }

  return (
    <div className="max-w-7xl space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold heading mb-2">Minha Conta</h1>
        <p className="text-sm opacity-70">Gerencie suas informações pessoais e assinatura</p>
      </div>

      {/* Layout em Grid */}
      <div className="grid lg:grid-cols-3 gap-6">

        {/* Coluna Esquerda - Info Pessoal + Status */}
        <div className="lg:col-span-1 space-y-6">

          {/* Card de Status */}
          <div className="panel p-6 space-y-4">
            <div className="text-center pb-4 border-b border-[var(--cg-border)]">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center mx-auto mb-3">
                <i className="fa-solid fa-user text-white text-3xl"></i>
              </div>
              <h3 className="font-bold text-lg heading">{user?.name || user?.email}</h3>
              <p className="text-xs opacity-60 mt-1">{user?.email}</p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2">
                <span className="text-sm opacity-70">Plano</span>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
                  user?.plan?.name === 'Pro'
                    ? 'bg-gradient-to-r from-brand-600 to-brand-500 text-white'
                    : 'bg-slate-700 text-slate-300'
                }`}>
                  {user?.plan?.name === 'Pro' && <i className="fa-solid fa-crown"></i>}
                  {user?.plan?.name || 'Teste Gratuito'}
                </span>
              </div>

              <div className="flex items-center justify-between py-2">
                <span className="text-sm opacity-70">Status</span>
                <span className={`text-xs font-medium px-2 py-1 rounded ${
                  user?.subscriptionStatus === 'Ativa' ? 'bg-green-500/20 text-green-400' :
                  user?.subscriptionStatus === 'Cancelada' ? 'bg-red-500/20 text-red-400' :
                  user?.subscriptionStatus === 'Inadimplente' ? 'bg-yellow-500/20 text-yellow-400' :
                  user?.subscriptionStatus === 'Aguardando Pagamento' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-slate-700 text-slate-300'
                }`}>
                  {user?.subscriptionStatus || 'Sem assinatura'}
                </span>
              </div>

              {user?.currentCycleEnd && user?.plan?.name === 'Pro' && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm opacity-70">Renovação</span>
                  <span className="text-sm">
                    {new Date(user.currentCycleEnd).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              )}

              {user?.credits !== undefined && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm opacity-70">Créditos</span>
                  <span className="text-sm font-bold" style={{color:'var(--cg-accent)'}}>
                    {user.credits} disponíveis
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Informações Pessoais */}
          <div className="panel p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Informações Pessoais</h3>
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="btn btn-ghost h-8 px-3 text-xs"
                >
                  <i className="fa-solid fa-pen"></i>
                </button>
              )}
            </div>

            {editing ? (
              <form onSubmit={handleUpdateProfile} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium mb-1.5 opacity-70">Nome completo</label>
                  <input
                    className="input w-full text-sm"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    type="text"
                    placeholder="Seu nome completo"
                    required
                    minLength={3}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5 opacity-70">Telefone</label>
                  <input
                    className="input w-full text-sm"
                    value={phone}
                    onChange={e => setPhone(phoneMask(e.target.value))}
                    type="tel"
                    placeholder="(11) 99999-9999"
                    maxLength={15}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5 opacity-70">CPF</label>
                  <input
                    className="input w-full text-sm"
                    value={cpf}
                    onChange={e => setCpf(cpfMask(e.target.value))}
                    type="text"
                    placeholder="000.000.000-00"
                    maxLength={14}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5 opacity-70">Data de nascimento</label>
                  <input
                    className="input w-full text-sm"
                    value={birthDate}
                    onChange={e => setBirthDate(e.target.value)}
                    type="date"
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    type="submit"
                    disabled={loading === 'update'}
                    className="btn btn-primary h-9 px-4 text-xs disabled:opacity-60 flex-1"
                  >
                    {loading === 'update' ? (
                      <><i className="fa-solid fa-spinner fa-spin"></i> Salvando...</>
                    ) : (
                      <><i className="fa-solid fa-check"></i> Salvar</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    disabled={loading === 'update'}
                    className="btn btn-outline h-9 px-4 text-xs disabled:opacity-60"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-xs opacity-60 mb-1">Telefone</div>
                  <div className="font-medium">{user?.phone || 'Não informado'}</div>
                </div>
                <div>
                  <div className="text-xs opacity-60 mb-1">CPF</div>
                  <div className="font-medium">{user?.cpf || 'Não informado'}</div>
                </div>
                <div>
                  <div className="text-xs opacity-60 mb-1">Data de Nascimento</div>
                  <div className="font-medium">
                    {user?.birthDate ? new Date(user.birthDate).toLocaleDateString('pt-BR') : 'Não informado'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Coluna Direita - Assinatura (2 colunas) */}
        <div className="lg:col-span-2">
          <div className="panel p-8 space-y-8">

            {/* Header da Assinatura */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center flex-shrink-0">
                  <i className="fa-solid fa-crown text-white text-2xl"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold heading mb-1">Plano Pro</h2>
                  <p className="text-sm opacity-70">
                    Turbine sua produção de conteúdo com IA avançada
                  </p>
                </div>
              </div>
            </div>

            {/* Grid de Benefícios */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-[var(--cg-surface-alt)]">
                <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <i className="fa-solid fa-coins text-green-400 text-lg"></i>
                </div>
                <div>
                  <div className="font-semibold text-sm mb-1">100 créditos mensais</div>
                  <div className="text-xs opacity-70">Renovação automática todo mês</div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-xl bg-[var(--cg-surface-alt)]">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <i className="fa-solid fa-wand-magic-sparkles text-purple-400 text-lg"></i>
                </div>
                <div>
                  <div className="font-semibold text-sm mb-1">Todos os especialistas</div>
                  <div className="text-xs opacity-70">E-commerce, Social Media e Livre</div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-xl bg-[var(--cg-surface-alt)]">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <i className="fa-solid fa-star text-blue-400 text-lg"></i>
                </div>
                <div>
                  <div className="font-semibold text-sm mb-1">Copy otimizada</div>
                  <div className="text-xs opacity-70">Textos profissionais prontos para usar</div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-xl bg-[var(--cg-surface-alt)]">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <i className="fa-solid fa-shield-halved text-emerald-400 text-lg"></i>
                </div>
                <div>
                  <div className="font-semibold text-sm mb-1">Flexibilidade total</div>
                  <div className="text-xs opacity-70">Cancele quando quiser, sem multas</div>
                </div>
              </div>
            </div>

            <div className="h-px bg-[var(--cg-border)]"></div>

            {user?.subscriptionStatus === 'Aguardando Pagamento' ? (
              <div className="space-y-5">
                <div className="p-5 rounded-2xl border-2 border-yellow-500/30 bg-yellow-500/5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                      <i className="fa-solid fa-clock text-yellow-400 text-2xl"></i>
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-base mb-1">Aguardando Confirmação de Pagamento</div>
                      <div className="text-sm opacity-70">
                        Estamos verificando seu pagamento automaticamente. Isso pode levar alguns minutos.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="panel-alt p-5">
                  <div className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-3">Ações</div>
                  <div className="space-y-3">
                    {paymentResult && (
                      <button
                        onClick={() => setShowPaymentModal(true)}
                        className="btn btn-primary h-11 px-5 text-sm w-full"
                      >
                        <i className="fa-solid fa-qrcode"></i> Ver forma de pagamento
                      </button>
                    )}

                    <button
                      disabled={checkingPayment}
                      onClick={async () => {
                        setCheckingPayment(true);
                        try {
                          await refresh();
                          if (user?.subscriptionStatus === 'Ativa') {
                            push('Pagamento confirmado!', 'success');
                          } else {
                            push('Ainda aguardando confirmação...', 'info');
                          }
                        } catch (e) {
                          push('Erro ao verificar pagamento', 'error');
                        } finally {
                          setCheckingPayment(false);
                        }
                      }}
                      className="btn btn-outline h-11 px-5 text-sm w-full"
                    >
                      {checkingPayment ? (
                        <><i className="fa-solid fa-spinner fa-spin"></i> Verificando...</>
                      ) : (
                        <><i className="fa-solid fa-sync"></i> Já paguei, verificar agora</>
                      )}
                    </button>
                  </div>

                  <div className="h-px bg-[var(--cg-border)] my-4"></div>

                  <div className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-3">Mudar forma de pagamento</div>
                  <button
                    disabled={loading === 'cancelar'}
                    onClick={async () => {
                      if (window.confirm('Deseja cancelar este pagamento pendente e escolher outra forma de pagamento?\n\nSe você já pagou, aguarde a confirmação. Caso cancele, precisará gerar um novo pagamento.')) {
                        try {
                          setJustCancelled(true); // Marca que acabou de cancelar
                          await wrap('cancelar', async () => {
                            await api.cancelSubscription(token!);
                          });
                          setPaymentResult(null);
                          localStorage.removeItem('pending_payment');
                          await refresh(); // Atualiza dados do usuário
                          push('Pagamento cancelado. Escolha outra forma de pagamento abaixo.', 'success');
                          // Reseta a flag após 3 segundos
                          setTimeout(() => setJustCancelled(false), 3000);
                        } catch (e: any) {
                          console.error('[FRONTEND] Erro ao cancelar:', e);
                          push('Erro ao cancelar: ' + e.message, 'error');
                          setJustCancelled(false);
                        }
                      }
                    }}
                    className="btn btn-outline h-11 px-5 text-sm w-full border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                  >
                    {loading === 'cancelar' ? (
                      <><i className="fa-solid fa-spinner fa-spin"></i> Cancelando...</>
                    ) : (
                      <><i className="fa-solid fa-exchange-alt"></i> Escolher outra forma de pagamento</>
                    )}
                  </button>

                  <p className="text-xs text-center opacity-60 mt-3">
                    <i className="fa-solid fa-info-circle"></i> Verificação automática acontece a cada 10 segundos
                  </p>
                </div>
              </div>
            ) : user?.plan?.name === 'Pro' && user?.subscriptionStatus === 'Ativa' ? (
              <div className="space-y-5">
                <div className="p-5 rounded-2xl border-2 border-green-500/30 bg-green-500/5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <i className="fa-solid fa-check-circle text-green-400 text-2xl"></i>
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-base mb-1">Assinatura Pro Ativa</div>
                      <div className="text-sm opacity-70">Você tem acesso completo a todos os recursos premium com {user.credits} créditos disponíveis</div>
                    </div>
                  </div>
                </div>

                <div className="panel-alt p-5">
                  <div className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-3">Gerenciar Assinatura</div>
                  <button
                    disabled={loading==='cancelar'}
                    onClick={()=>wrap('cancelar', async ()=>{ await api.cancelSubscription(token!); })}
                    className="btn btn-outline h-11 px-5 text-sm disabled:opacity-60 w-full border-red-500/30 text-red-400 hover:bg-red-500/10 transition-all"
                  >
                    {loading==='cancelar'? (
                      <><i className="fa-solid fa-spinner fa-spin"></i> Cancelando...</>
                    ) : (
                      <><i className="fa-solid fa-times-circle"></i> Cancelar Assinatura</>
                    )}
                  </button>
                  <p className="text-xs text-center opacity-60 mt-3">
                    <i className="fa-solid fa-info-circle"></i> Você mantém acesso até o fim do período pago
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-7">
                {/* Mensagem se assinatura foi cancelada */}
                {user?.subscriptionStatus === 'Cancelada' && (
                  <div className="p-5 rounded-2xl border-2 border-orange-500/30 bg-orange-500/5">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                        <i className="fa-solid fa-info-circle text-orange-400 text-2xl"></i>
                      </div>
                      <div className="flex-1">
                        <div className="font-bold text-base mb-1">Assinatura Cancelada</div>
                        <div className="text-sm opacity-70">
                          Sua assinatura foi cancelada. Você pode assinar novamente a qualquer momento!
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Seleção de Ciclo */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-base font-bold mb-1">1. Escolha a periodicidade</div>
                      <div className="text-xs opacity-60">Plano anual recomendado - melhor custo-benefício</div>
                    </div>
                    <span className="text-xs bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1.5 rounded-full font-bold">
                      <i className="fa-solid fa-sparkles"></i> RECOMENDADO
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setSelectedCycle('MONTHLY')}
                      className={`p-6 rounded-2xl border-2 transition-all text-left group hover:shadow-lg hover:scale-[1.02] ${
                        selectedCycle === 'MONTHLY'
                          ? 'border-brand-500 bg-brand-500/10 shadow-lg'
                          : 'border-[var(--cg-border)] hover:border-brand-500/50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 rounded-xl bg-brand-500/20 flex items-center justify-center">
                          <i className="fa-solid fa-calendar text-brand-400 text-lg"></i>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          selectedCycle === 'MONTHLY'
                            ? 'border-brand-500 bg-brand-500'
                            : 'border-[var(--cg-border)]'
                        }`}>
                          {selectedCycle === 'MONTHLY' && (
                            <i className="fa-solid fa-check text-white text-xs"></i>
                          )}
                        </div>
                      </div>
                      <div className="text-lg font-bold mb-1">Plano Mensal</div>
                      <div className="flex items-baseline gap-1 mb-2">
                        <span className="text-3xl font-bold" style={{color:'var(--cg-accent)'}}>R$ 39,90</span>
                        <span className="text-sm opacity-60">/mês</span>
                      </div>
                      <div className="text-xs opacity-60">
                        Renovação automática mensal
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setSelectedCycle('YEARLY')}
                      className={`p-6 rounded-2xl border-2 transition-all text-left relative group hover:shadow-lg hover:scale-[1.02] ${
                        selectedCycle === 'YEARLY'
                          ? 'border-brand-500 bg-brand-500/10 shadow-lg'
                          : 'border-[var(--cg-border)] hover:border-brand-500/50'
                      }`}
                    >
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-[10px] px-3 py-1.5 rounded-full font-bold shadow-lg">
                        <i className="fa-solid fa-piggy-bank mr-1"></i> ECONOMIZE R$ 78,80
                      </div>
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                          <i className="fa-solid fa-calendar-days text-emerald-400 text-lg"></i>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          selectedCycle === 'YEARLY'
                            ? 'border-brand-500 bg-brand-500'
                            : 'border-[var(--cg-border)]'
                        }`}>
                          {selectedCycle === 'YEARLY' && (
                            <i className="fa-solid fa-check text-white text-xs"></i>
                          )}
                        </div>
                      </div>
                      <div className="text-lg font-bold mb-1">Plano Anual</div>
                      <div className="flex items-baseline gap-1 mb-2">
                        <span className="text-3xl font-bold" style={{color:'var(--cg-accent)'}}>R$ 400</span>
                        <span className="text-sm opacity-60">/ano</span>
                      </div>
                      <div className="text-xs">
                        <span className="opacity-60">Apenas </span>
                        <span className="font-bold text-green-400">R$ 33,33/mês</span>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Seleção de Forma de Pagamento */}
                <div>
                  <div className="mb-4">
                    <div className="text-base font-bold mb-1">2. Método de pagamento</div>
                    <div className="text-xs opacity-60">Escolha como prefere pagar sua assinatura</div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setSelectedPayment('CREDIT_CARD')}
                      className={`p-5 rounded-xl border-2 transition-all text-center group hover:shadow-md hover:scale-[1.02] ${
                        selectedPayment === 'CREDIT_CARD'
                          ? 'border-brand-500 bg-brand-500/10'
                          : 'border-[var(--cg-border)] hover:border-brand-500/50'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center transition-all ${
                        selectedPayment === 'CREDIT_CARD' ? 'bg-brand-500/20' : 'bg-[var(--cg-surface-alt)]'
                      }`}>
                        <i className={`fa-solid fa-credit-card text-xl ${
                          selectedPayment === 'CREDIT_CARD' ? 'text-brand-400' : 'opacity-60'
                        }`}></i>
                      </div>
                      <div className="text-sm font-bold mb-1">Cartão</div>
                      <div className="text-xs opacity-60">Pagamento automático</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setSelectedPayment('PIX')}
                      className={`p-5 rounded-xl border-2 transition-all text-center group hover:shadow-md hover:scale-[1.02] ${
                        selectedPayment === 'PIX'
                          ? 'border-brand-500 bg-brand-500/10'
                          : 'border-[var(--cg-border)] hover:border-brand-500/50'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center transition-all ${
                        selectedPayment === 'PIX' ? 'bg-brand-500/20' : 'bg-[var(--cg-surface-alt)]'
                      }`}>
                        <i className={`fa-brands fa-pix text-xl ${
                          selectedPayment === 'PIX' ? 'text-brand-400' : 'opacity-60'
                        }`}></i>
                      </div>
                      <div className="text-sm font-bold mb-1">PIX</div>
                      <div className="text-xs opacity-60">Aprovação instantânea</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setSelectedPayment('BOLETO')}
                      className={`p-5 rounded-xl border-2 transition-all text-center group hover:shadow-md hover:scale-[1.02] ${
                        selectedPayment === 'BOLETO'
                          ? 'border-brand-500 bg-brand-500/10'
                          : 'border-[var(--cg-border)] hover:border-brand-500/50'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center transition-all ${
                        selectedPayment === 'BOLETO' ? 'bg-brand-500/20' : 'bg-[var(--cg-surface-alt)]'
                      }`}>
                        <i className={`fa-solid fa-barcode text-xl ${
                          selectedPayment === 'BOLETO' ? 'text-brand-400' : 'opacity-60'
                        }`}></i>
                      </div>
                      <div className="text-sm font-bold mb-1">Boleto</div>
                      <div className="text-xs opacity-60">Pagamento bancário</div>
                    </button>
                  </div>
                </div>

                {/* Campo de CPF para PIX/Boleto (se não tiver cadastrado) */}
                {(selectedPayment === 'PIX' || selectedPayment === 'BOLETO') && !user?.cpf && (
                  <div className="panel-alt p-6 rounded-2xl">
                    <div className="text-sm font-bold mb-3">3. Dados pessoais</div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5 opacity-70">
                        CPF/CNPJ <span className="text-red-400">*</span>
                      </label>
                      <input
                        className="input w-full text-sm"
                        value={cpf}
                        onChange={e => setCpf(cpfMask(e.target.value))}
                        placeholder="000.000.000-00"
                        maxLength={18}
                      />
                      <div className="text-[10px] opacity-50 mt-1">
                        Obrigatório para emissão de cobrança
                      </div>
                    </div>
                  </div>
                )}

                {/* Formulário de Cartão (se selecionado) */}
                {selectedPayment === 'CREDIT_CARD' && (
                  <div className="panel-alt p-6 rounded-2xl space-y-4">
                    <div className="text-sm font-bold mb-3">3. Dados do cartão de crédito</div>

                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1.5 opacity-70">
                          Nome no cartão <span className="text-red-400">*</span>
                        </label>
                        <input
                          className="input w-full text-sm"
                          value={cardData.holderName}
                          onChange={e => setCardData({...cardData, holderName: e.target.value})}
                          placeholder="NOME COMO ESTÁ NO CARTÃO"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium mb-1.5 opacity-70">
                          Número do cartão <span className="text-red-400">*</span>
                        </label>
                        <input
                          className="input w-full text-sm"
                          value={cardData.number}
                          onChange={e => setCardData({...cardData, number: e.target.value.replace(/\D/g, '').replace(/(.{4})/g, '$1 ').trim()})}
                          placeholder="0000 0000 0000 0000"
                          maxLength={19}
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1.5 opacity-70">
                            Mês <span className="text-red-400">*</span>
                          </label>
                          <input
                            className="input w-full text-sm"
                            value={cardData.expiryMonth}
                            onChange={e => setCardData({...cardData, expiryMonth: e.target.value.replace(/\D/g, '')})}
                            placeholder="MM"
                            maxLength={2}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1.5 opacity-70">
                            Ano <span className="text-red-400">*</span>
                          </label>
                          <input
                            className="input w-full text-sm"
                            value={cardData.expiryYear}
                            onChange={e => setCardData({...cardData, expiryYear: e.target.value.replace(/\D/g, '')})}
                            placeholder="AAAA"
                            maxLength={4}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1.5 opacity-70">
                            CVV <span className="text-red-400">*</span>
                          </label>
                          <input
                            className="input w-full text-sm"
                            value={cardData.ccv}
                            onChange={e => setCardData({...cardData, ccv: e.target.value.replace(/\D/g, '')})}
                            placeholder="123"
                            maxLength={4}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div>
                          <label className="block text-xs font-medium mb-1.5 opacity-70">
                            CEP <span className="text-red-400">*</span>
                          </label>
                          <input
                            className="input w-full text-sm"
                            value={cardHolderInfo.postalCode}
                            onChange={e => setCardHolderInfo({...cardHolderInfo, postalCode: e.target.value.replace(/\D/g, '')})}
                            placeholder="00000-000"
                            maxLength={8}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1.5 opacity-70">
                            Número <span className="text-red-400">*</span>
                          </label>
                          <input
                            className="input w-full text-sm"
                            value={cardHolderInfo.addressNumber}
                            onChange={e => setCardHolderInfo({...cardHolderInfo, addressNumber: e.target.value})}
                            placeholder="123"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Resumo e CTA */}
                <div className="space-y-4">
                  <div className="panel-alt p-6 rounded-2xl">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-sm font-bold">Resumo do pedido</div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="opacity-70">Periodicidade:</span>
                        <span className="font-semibold">{selectedCycle === 'YEARLY' ? 'Pagamento Anual' : 'Pagamento Mensal'}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="opacity-70">Método de pagamento:</span>
                        <span className="font-semibold">
                          {selectedPayment === 'CREDIT_CARD' ? 'Cartão de Crédito' : selectedPayment === 'PIX' ? 'PIX' : 'Boleto Bancário'}
                        </span>
                      </div>
                      <div className="h-px bg-[var(--cg-border)] my-3"></div>
                      <div className="flex items-center justify-between">
                        <span className="text-base font-medium">Valor total:</span>
                        <div className="text-right">
                          <div className="text-3xl font-bold" style={{color:'var(--cg-accent)'}}>
                            {selectedCycle === 'YEARLY' ? 'R$ 400' : 'R$ 39,90'}
                          </div>
                          <div className="text-xs opacity-60">
                            {selectedCycle === 'YEARLY' ? 'Cobrado anualmente' : 'Cobrado mensalmente'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    disabled={loading==='assinar'}
                    onClick={async ()=>{
                      console.log('[FRONTEND] 🚀 Botão clicado!');
                      console.log('[FRONTEND] Token existe?', !!token);
                      console.log('[FRONTEND] Ciclo selecionado:', selectedCycle);
                      console.log('[FRONTEND] Pagamento selecionado:', selectedPayment);

                      try {
                        // Validação de CPF obrigatório
                        const userCpf = user?.cpf || cpf;
                        if (!userCpf && selectedPayment !== 'CREDIT_CARD') {
                          showErrorModal(
                            'CPF/CNPJ é obrigatório para criar uma assinatura.',
                            {
                              title: 'CPF não informado',
                              code: 'CPF_REQUIRED',
                              suggestion: 'Preencha seu CPF no campo acima para continuar com o pagamento.'
                            }
                          );
                          return;
                        }

                        // Validação do formato do CPF
                        if (!user?.cpf && cpf && unmask(cpf).length < 11) {
                          showErrorModal(
                            'CPF/CNPJ inválido. Verifique o número informado.',
                            {
                              title: 'CPF inválido',
                              suggestion: 'O CPF deve ter 11 dígitos e o CNPJ 14 dígitos.'
                            }
                          );
                          return;
                        }

                        // Prepara dados do cartão se necessário
                        let creditCard = undefined;
                        let creditCardHolderInfo = undefined;

                        if (selectedPayment === 'CREDIT_CARD') {
                          if (!cardData.holderName || !cardData.number || !cardData.expiryMonth || !cardData.expiryYear || !cardData.ccv) {
                            showErrorModal('Preencha todos os dados do cartão de crédito.', {
                              title: 'Dados incompletos',
                              suggestion: 'Verifique se preencheu nome, número, validade e CVV do cartão.'
                            });
                            return;
                          }
                          if (!cardHolderInfo.postalCode || !cardHolderInfo.addressNumber) {
                            showErrorModal('Preencha CEP e número do endereço.', {
                              title: 'Endereço incompleto',
                              suggestion: 'O CEP e número são obrigatórios para pagamento com cartão de crédito.'
                            });
                            return;
                          }
                          if (!cardHolderInfo.cpfCnpj) {
                            showErrorModal('CPF/CNPJ é obrigatório para pagamento com cartão.', {
                              title: 'CPF não informado',
                              suggestion: 'Preencha o CPF/CNPJ nos dados do titular do cartão.'
                            });
                            return;
                          }

                          creditCard = cardData;
                          creditCardHolderInfo = cardHolderInfo;
                        }

                        console.log('[FRONTEND] 📞 Chamando API...');

                        // Prepara CPF para PIX/Boleto
                        const checkoutCpf = (selectedPayment !== 'CREDIT_CARD' && cpf) ? unmask(cpf) : undefined;

                        const result = await wrap('assinar', async ()=>{
                          return await api.createSubscription('Pro', selectedCycle, selectedPayment, token!, creditCard, creditCardHolderInfo, checkoutCpf);
                        });

                        console.log('[FRONTEND] ✅ Resultado recebido:', result);
                        console.log('[FRONTEND] tem payment?', !!result.payment);
                        console.log('[FRONTEND] tem pix?', !!result.pix);
                        console.log('[FRONTEND] tem bankSlipUrl?', !!result.bankSlipUrl);

                        // Verifica se já existe assinatura ativa
                        if (result.message?.includes('já existente')) {
                          console.log('[FRONTEND] ℹ️ Assinatura já existe');
                          push('Você já possui uma assinatura ativa!', 'info');
                          await refresh();
                          return;
                        }

                        // Verifica se tem pagamento pendente
                        if (result.message?.includes('pendente encontrado')) {
                          console.log('[FRONTEND] 💰 Pagamento pendente recuperado');
                          setPaymentResult(result);

                          if (result.pix) {
                            push('Pagamento PIX recuperado! Escaneie o QR Code para pagar.', 'info');
                            setShowPaymentModal(true);
                          } else if (result.bankSlipUrl) {
                            push('Boleto recuperado! Clique para visualizar.', 'info');
                            setShowPaymentModal(true);
                          } else if (result.billingType === 'CREDIT_CARD') {
                            push('Pagamento via cartão já processado. Aguarde a confirmação.', 'info');
                          } else {
                            push('Você já possui um pagamento pendente!', 'info');
                            setShowPaymentModal(true);
                          }
                          return;
                        }

                        // Valida se recebeu dados de pagamento
                        if (!result.payment && selectedPayment !== 'CREDIT_CARD') {
                          console.warn('[FRONTEND] ⚠️ Sem dados de pagamento, aguardando...');
                          push('Aguardando processamento do pagamento...', 'info');
                          // Tenta novamente após alguns segundos
                          setTimeout(() => {
                            refresh();
                          }, 3000);
                          return;
                        }

                        // Armazena resultado para exibir
                        setPaymentResult(result);
                        console.log('[FRONTEND] 💾 Resultado armazenado no estado');

                        // Exibe mensagem e modal apropriados
                        if (selectedPayment === 'CREDIT_CARD') {
                          push('Assinatura criada e pagamento processado!', 'success');
                          setShowPaymentModal(true);
                        } else if (selectedPayment === 'PIX') {
                          if (result.pix) {
                            push('PIX gerado! Escaneie o QR Code para pagar.', 'success');
                            setShowPaymentModal(true);
                            console.log('[FRONTEND] 🎉 Modal PIX aberto');
                          } else {
                            console.warn('[FRONTEND] ⚠️ PIX não disponível ainda');
                            push('Aguardando geração do PIX...', 'info');
                            setTimeout(() => refresh(), 3000);
                          }
                        } else if (selectedPayment === 'BOLETO') {
                          if (result.bankSlipUrl || result.payment?.bankSlipUrl) {
                            push('Boleto gerado! Clique para visualizar.', 'success');
                            setShowPaymentModal(true);
                          } else {
                            push('Aguardando geração do boleto...', 'info');
                            setTimeout(() => refresh(), 3000);
                          }
                        }
                      } catch (e: any) {
                        console.error('[FRONTEND] ❌ Erro capturado:', e);
                        console.error('[FRONTEND] Mensagem do erro:', e.message);
                        console.error('[FRONTEND] Stack:', e.stack);

                        // Determina sugestão baseada no erro
                        let suggestion = 'Tente novamente ou entre em contato com o suporte.';
                        let actionLabel = undefined;
                        let onAction = undefined;

                        const errorMsg = e.message?.toLowerCase() || '';

                        if (errorMsg.includes('cpf') || errorMsg.includes('cnpj')) {
                          suggestion = 'Atualize seu perfil com um CPF/CNPJ válido antes de criar a assinatura.';
                          actionLabel = 'Editar Perfil';
                          onAction = () => setEditing(true);
                        } else if (errorMsg.includes('cartão') || errorMsg.includes('card')) {
                          suggestion = 'Verifique se os dados do cartão estão corretos e se há limite disponível.';
                        } else if (errorMsg.includes('cep') || errorMsg.includes('postal')) {
                          suggestion = 'Verifique se o CEP informado é válido.';
                        }

                        showErrorModal(
                          e.message || 'Erro ao processar pagamento',
                          {
                            title: 'Falha no pagamento',
                            suggestion,
                            actionLabel,
                            onAction
                          }
                        );
                      }
                    }}
                    className="btn btn-primary h-14 px-6 text-base font-bold disabled:opacity-60 w-full shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
                  >
                    {loading==='assinar'? (
                      <><i className="fa-solid fa-spinner fa-spin mr-2"></i> Processando pedido...</>
                    ) : (
                      <>
                        <i className="fa-solid fa-rocket mr-2"></i>
                        {selectedPayment === 'CREDIT_CARD' ? 'Pagar com Cartão' : 'Gerar Pagamento'}
                      </>
                    )}
                  </button>

                  <div className="flex items-center justify-center gap-3 text-xs opacity-60">
                    <div className="flex items-center gap-1.5">
                      <i className="fa-solid fa-lock"></i>
                      <span>Pagamento Seguro</span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center gap-1.5">
                      <i className="fa-solid fa-shield-check"></i>
                      <span>Dados Protegidos</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="p-4 rounded-xl flex items-start gap-3 bg-red-500/10 border-2 border-red-500/30">
                <i className="fa-solid fa-triangle-exclamation text-red-400 text-lg mt-0.5"></i>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-red-400 mb-1">Erro ao processar</div>
                  <div className="text-xs text-red-300">{error}</div>
                </div>
              </div>
            )}

          </div>
        </div>

      </div>

      {/* Integração com Redes Sociais */}
      <div className="panel p-8">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
              <i className="fa-solid fa-share-nodes text-white text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold heading mb-1">Integração com Redes Sociais</h2>
              <p className="text-sm opacity-70">
                Conecte suas contas Meta para publicar automaticamente
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-dim">{advancedModeEnabled ? 'Ativado' : 'Desativado'}</span>
            <button
              onClick={advancedModeEnabled ? handleDisableAdvancedMode : handleEnableAdvancedMode}
              disabled={loadingMeta}
              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50"
              style={{
                backgroundColor: advancedModeEnabled ? 'var(--cg-accent)' : 'var(--cg-border-strong)'
              }}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  advancedModeEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {advancedModeEnabled && (
          <div className="space-y-6 pt-6" style={{ borderTop: '1px solid var(--cg-border)' }}>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold heading text-lg">Contas Conectadas</h3>
              <button
                onClick={handleConnectFacebook}
                className="btn btn-primary h-9 text-sm"
              >
                <i className="fa-solid fa-plus mr-2"></i>
                Conectar Conta
              </button>
            </div>

            {loadingAccounts ? (
              <div className="text-center py-12">
                <i className="fa-solid fa-spinner fa-spin opacity-50 text-2xl"></i>
              </div>
            ) : connectedAccounts.length === 0 ? (
              <div className="text-center py-12 rounded-lg" style={{ backgroundColor: 'var(--cg-bg-alt)' }}>
                <i className="fa-solid fa-link-slash text-4xl mb-4 opacity-30"></i>
                <p className="text-dim text-sm">Nenhuma conta conectada ainda</p>
                <p className="text-xs text-dim mt-1">Clique em "Conectar Conta" para começar</p>
              </div>
            ) : (
              <div className="space-y-3">
                {connectedAccounts.map(account => (
                  <div
                    key={account._id}
                    className="flex items-center gap-4 p-5 rounded-xl"
                    style={{ backgroundColor: 'var(--cg-bg-alt)' }}
                  >
                    {account.profilePictureUrl && (
                      <img
                        src={account.profilePictureUrl}
                        alt={account.accountName}
                        className="w-14 h-14 rounded-full"
                      />
                    )}
                    <div className="flex-1">
                      <div className="font-semibold text-base heading">{account.accountName}</div>
                      <div className="text-sm text-dim flex items-center gap-2 mt-1">
                        {account.platform === 'instagram' ? (
                          <>
                            <i className="fa-brands fa-instagram text-pink-400"></i>
                            <span>Instagram {account.accountUsername && `(@${account.accountUsername})`}</span>
                          </>
                        ) : (
                          <>
                            <i className="fa-brands fa-facebook text-blue-400"></i>
                            <span>Facebook Page</span>
                          </>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDisconnect(account._id, account.accountName)}
                      className="text-sm px-4 py-2 rounded-md transition-colors"
                      style={{
                        color: 'var(--cg-danger)',
                        backgroundColor: 'var(--cg-danger-bg)'
                      }}
                    >
                      <i className="fa-solid fa-unlink mr-1"></i>
                      Desconectar
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="p-5 rounded-xl text-xs" style={{
              backgroundColor: 'var(--cg-accent-soft)',
              border: '1px solid var(--cg-border)'
            }}>
              <strong className="heading block mb-2"><i className="fa-solid fa-clipboard-list mr-1"></i> Requisitos:</strong>
              <ul className="space-y-1.5 text-dim">
                <li>• <strong>Instagram:</strong> Conta Business ou Creator vinculada a uma Página do Facebook</li>
                <li>• <strong>Facebook:</strong> Página (não perfil pessoal) com permissões de administrador</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Zona de Perigo */}
      {user?.plan?.name === 'Pro' && user?.subscriptionStatus === 'Ativa' && (
        <div className="mt-12">
          <div className="panel border-2 border-red-500/30 p-6 bg-red-500/5">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <i className="fa-solid fa-triangle-exclamation text-red-400 text-2xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-red-400 mb-2">Zona de Perigo</h3>
                <p className="text-sm opacity-80">
                  Cancelar sua assinatura significa que você perderá acesso aos recursos Pro no final do período atual.
                  Seus créditos serão redefinidos e você voltará ao plano gratuito.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                <div className="flex items-start gap-3 mb-3">
                  <i className="fa-solid fa-exclamation-circle text-red-400 text-lg mt-0.5"></i>
                  <div className="flex-1">
                    <div className="font-semibold text-red-400 mb-2">O que acontece ao cancelar:</div>
                    <ul className="space-y-1.5 text-sm opacity-90">
                      <li>• Você mantém acesso Pro até {user?.currentCycleEnd ? new Date(user.currentCycleEnd).toLocaleDateString('pt-BR') : 'o fim do ciclo'}</li>
                      <li>• Após esta data, voltará automaticamente ao plano gratuito (10 créditos)</li>
                      <li>• Você não será mais cobrado nas próximas renovações</li>
                      <li>• Pode reativar a assinatura a qualquer momento</li>
                    </ul>
                  </div>
                </div>
              </div>

              <button
                disabled={loading==='cancelar'}
                onClick={async ()=>{
                  const confirmText = 'Você tem certeza que deseja CANCELAR sua assinatura Pro?\n\nATENÇÃO:\n• Você perderá acesso aos recursos Pro após ' +
                    (user?.currentCycleEnd ? new Date(user.currentCycleEnd).toLocaleDateString('pt-BR') : 'o fim do ciclo atual') +
                    '\n• Seus créditos serão redefinidos para 10 (plano gratuito)\n• As cobranças serão interrompidas\n\nEsta ação NÃO pode ser desfeita automaticamente.\n\nDigite "CANCELAR" para confirmar:';

                  const userInput = window.prompt(confirmText);

                  if (userInput === 'CANCELAR') {
                    try {
                      await wrap('cancelar', async ()=>{
                        await api.cancelSubscription(token!);
                      });
                      push('Assinatura cancelada com sucesso. Você mantém acesso até o fim do ciclo.', 'success');
                    } catch (e: any) {
                      push('Erro ao cancelar: ' + e.message, 'error');
                    }
                  } else if (userInput !== null) {
                    push('Cancelamento não confirmado. Digite exatamente "CANCELAR" para confirmar.', 'info');
                  }
                }}
                className="btn h-12 px-6 text-sm disabled:opacity-60 w-full bg-red-500/10 border-2 border-red-500/50 text-red-400 hover:bg-red-500/20 hover:border-red-500 transition-all font-bold"
              >
                {loading==='cancelar'? (
                  <><i className="fa-solid fa-spinner fa-spin mr-2"></i> Cancelando Assinatura...</>
                ) : (
                  <><i className="fa-solid fa-ban mr-2"></i> Cancelar Assinatura Pro</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacidade e Dados (LGPD) */}
      <div className="panel p-8">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
            <i className="fa-solid fa-shield-halved text-white text-2xl"></i>
          </div>
          <div>
            <h2 className="text-2xl font-bold heading mb-1">Privacidade e Dados</h2>
            <p className="text-sm opacity-70">
              Gerencie seus dados pessoais conforme a LGPD (Lei Geral de Proteção de Dados)
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Exportar Dados */}
          <div className="p-6 rounded-2xl bg-[var(--cg-surface-alt)] border border-[var(--cg-border)]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <i className="fa-solid fa-download text-blue-400 text-lg"></i>
              </div>
              <div>
                <div className="font-semibold text-sm">Exportar Meus Dados</div>
                <div className="text-xs opacity-60">Art. 18, V - Direito à Portabilidade</div>
              </div>
            </div>
            <p className="text-xs opacity-70 mb-4">
              Baixe todos os seus dados pessoais em formato JSON, incluindo perfil, gerações, conversas e histórico de pagamentos.
            </p>
            <button
              onClick={handleExportData}
              disabled={loadingExport}
              className="btn btn-outline h-10 px-4 text-sm w-full"
            >
              {loadingExport ? (
                <><i className="fa-solid fa-spinner fa-spin mr-2"></i> Exportando...</>
              ) : (
                <><i className="fa-solid fa-file-export mr-2"></i> Exportar Dados</>
              )}
            </button>
          </div>

          {/* Deletar Conta */}
          <div className="p-6 rounded-2xl bg-red-500/5 border border-red-500/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                <i className="fa-solid fa-user-slash text-red-400 text-lg"></i>
              </div>
              <div>
                <div className="font-semibold text-sm text-red-400">Deletar Minha Conta</div>
                <div className="text-xs opacity-60">Art. 18, VI - Direito à Eliminação</div>
              </div>
            </div>
            <p className="text-xs opacity-70 mb-4">
              Exclua permanentemente sua conta e todos os dados associados. Esta ação é irreversível.
            </p>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="btn h-10 px-4 text-sm w-full bg-red-500/10 border border-red-500/50 text-red-400 hover:bg-red-500/20"
            >
              <i className="fa-solid fa-trash-alt mr-2"></i> Deletar Conta
            </button>
          </div>
        </div>

        <div className="mt-6 p-4 rounded-xl bg-[var(--cg-surface-alt)] text-xs opacity-70">
          <strong>Sobre a LGPD:</strong> A Lei Geral de Proteção de Dados (Lei nº 13.709/2018) garante a você o direito de acessar, corrigir, exportar e deletar seus dados pessoais a qualquer momento.
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteModal(false)}>
          <div className="panel p-8 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <i className="fa-solid fa-exclamation-triangle text-red-400 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-red-400 mb-2">Deletar Conta Permanentemente</h3>
              <p className="text-sm opacity-70">
                Esta ação é <strong>irreversível</strong>. Todos os seus dados serão permanentemente excluídos.
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                <div className="text-sm font-semibold text-red-400 mb-2">O que será deletado:</div>
                <ul className="space-y-1.5 text-xs opacity-90">
                  <li>• Seu perfil e informações pessoais</li>
                  <li>• Todas as gerações de conteúdo</li>
                  <li>• Histórico de conversas</li>
                  <li>• Contas sociais conectadas</li>
                  <li>• Assinatura e histórico de pagamentos</li>
                </ul>
              </div>

              <div>
                <label className="block text-xs font-medium mb-2 opacity-70">
                  Digite <strong className="text-red-400">DELETAR MINHA CONTA</strong> para confirmar:
                </label>
                <input
                  type="text"
                  className="input w-full text-sm"
                  value={deleteConfirmation}
                  onChange={e => setDeleteConfirmation(e.target.value)}
                  placeholder="DELETAR MINHA CONTA"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation('');
                }}
                className="btn btn-outline h-11 px-5 text-sm flex-1"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={loadingDelete || deleteConfirmation !== 'DELETAR MINHA CONTA'}
                className="btn h-11 px-5 text-sm flex-1 bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingDelete ? (
                  <><i className="fa-solid fa-spinner fa-spin mr-2"></i> Deletando...</>
                ) : (
                  <><i className="fa-solid fa-trash-alt mr-2"></i> Confirmar Exclusão</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Resultado do Pagamento */}
      {showPaymentModal && paymentResult && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowPaymentModal(false)}>
          <div className="panel p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">
                {paymentResult.pix ? 'Pague com PIX' :
                 paymentResult.bankSlipUrl ? 'Boleto Gerado' :
                 'Pagamento Processado'}
              </h3>
              <button onClick={() => setShowPaymentModal(false)} className="btn btn-ghost w-8 h-8 p-0">
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            {/* PIX QR Code */}
            {paymentResult.pix && paymentResult.pix.encodedImage && (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-sm opacity-70 mb-4">Escaneie o QR Code para pagar via PIX</div>
                  {paymentResult.pix.encodedImage && (
                    <div className="bg-white p-4 rounded-xl inline-block">
                      <img
                        src={`data:image/png;base64,${paymentResult.pix.encodedImage}`}
                        alt="QR Code PIX"
                        className="w-64 h-64"
                      />
                    </div>
                  )}
                  <div className="text-3xl font-bold my-4" style={{color:'var(--cg-accent)'}}>
                    R$ {paymentResult.value}
                  </div>
                </div>

                {paymentResult.pix.qrCode && (
                  <div>
                    <label className="block text-xs font-medium mb-2 opacity-70">Ou copie o código PIX:</label>
                    <div className="flex gap-2">
                      <input
                        className="input flex-1 text-xs font-mono"
                        value={paymentResult.pix.qrCode}
                        readOnly
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(paymentResult.pix.qrCode);
                          push('Código PIX copiado!', 'success');
                        }}
                        className="btn btn-primary px-4"
                      >
                        <i className="fa-solid fa-copy"></i>
                      </button>
                    </div>
                  </div>
                )}

                {paymentResult.pix.expirationDate && (
                  <div className="text-xs text-center opacity-60 mt-4">
                    Válido até: {new Date(paymentResult.pix.expirationDate).toLocaleString('pt-BR')}
                  </div>
                )}

                <div className="mt-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                  <div className="flex items-start gap-3">
                    <i className="fa-solid fa-info-circle text-blue-400 text-lg mt-0.5"></i>
                    <div className="text-sm">
                      <div className="font-semibold text-blue-400 mb-2">Após pagar o PIX:</div>
                      <ul className="space-y-1 opacity-80 text-xs">
                        <li>• A confirmação geralmente leva de 1 a 5 minutos</li>
                        <li>• Esta página verificará automaticamente a cada 10 segundos</li>
                        <li>• Você pode fechar esta janela e aguardar</li>
                        <li>• Quando confirmado, seu plano será ativado automaticamente</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Boleto */}
            {paymentResult.bankSlipUrl && !paymentResult.pix && (
              <div className="space-y-4 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto">
                  <i className="fa-solid fa-barcode text-blue-400 text-2xl"></i>
                </div>
                <div>
                  <div className="text-sm opacity-70 mb-2">Boleto gerado com sucesso!</div>
                  <div className="text-3xl font-bold mb-4" style={{color:'var(--cg-accent)'}}>
                    R$ {paymentResult.value}
                  </div>
                </div>
                <a
                  href={paymentResult.bankSlipUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary w-full h-12"
                >
                  <i className="fa-solid fa-file-pdf mr-2"></i>
                  Visualizar Boleto
                </a>
                <div className="text-xs opacity-60">
                  Vencimento: {new Date(paymentResult.payment?.dueDate).toLocaleDateString('pt-BR')}
                </div>

                <div className="mt-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 text-left">
                  <div className="flex items-start gap-3">
                    <i className="fa-solid fa-info-circle text-blue-400 text-lg mt-0.5"></i>
                    <div className="text-sm">
                      <div className="font-semibold text-blue-400 mb-2">Após pagar o boleto:</div>
                      <ul className="space-y-1 opacity-80 text-xs">
                        <li>• A confirmação leva de 1 a 3 dias úteis</li>
                        <li>• Esta página verificará automaticamente a cada 10 segundos</li>
                        <li>• Você pode fechar esta janela e voltar depois</li>
                        <li>• Quando confirmado, seu plano será ativado automaticamente</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cartão de Crédito */}
            {paymentResult.billingType === 'CREDIT_CARD' && !paymentResult.pix && !paymentResult.bankSlipUrl && (
              <div className="space-y-4 text-center">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                  <i className="fa-solid fa-check text-green-400 text-2xl"></i>
                </div>
                <div>
                  <div className="text-lg font-bold mb-2">Pagamento Processado!</div>
                  <div className="text-sm opacity-70 mb-4">
                    Sua assinatura foi ativada com sucesso
                  </div>
                  <div className="text-3xl font-bold" style={{color:'var(--cg-accent)'}}>
                    R$ {paymentResult.value}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    refresh();
                  }}
                  className="btn btn-primary w-full h-12"
                >
                  Continuar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Termos */}
      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
          <div className="panel max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {modalView === 'intro' && (
              <div className="p-6 space-y-6">
                <h2 className="text-2xl font-bold heading">Ativar Integração com Redes Sociais</h2>

                <div className="space-y-4">
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--cg-bg-alt)' }}>
                    <h3 className="font-semibold mb-2 heading">O que você poderá fazer:</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="fa-solid fa-check text-green-400 mt-1"></i>
                        <span>Publicar automaticamente no Instagram e Facebook</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="fa-solid fa-check text-green-400 mt-1"></i>
                        <span>Conectar múltiplas contas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="fa-solid fa-check text-green-400 mt-1"></i>
                        <span>Publicar carrosséis diretamente</span>
                      </li>
                    </ul>
                  </div>

                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--cg-accent-soft)', border: '1px solid var(--cg-accent)' }}>
                    <h3 className="font-semibold mb-2 heading"><i className="fa-solid fa-triangle-exclamation mr-1"></i> Importante:</h3>
                    <p className="text-sm text-dim">
                      Ao ativar, você concorda com nossos Termos de Uso e Política de Privacidade específicos para integração com Meta (Instagram/Facebook).
                    </p>
                  </div>

                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={e => setTermsAccepted(e.target.checked)}
                        className="mt-1"
                      />
                      <span className="text-sm">
                        Li e aceito os{' '}
                        <button
                          onClick={() => setModalView('terms')}
                          className="text-accent hover:underline"
                        >
                          Termos de Uso da Integração Meta
                        </button>
                      </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={privacyAccepted}
                        onChange={e => setPrivacyAccepted(e.target.checked)}
                        className="mt-1"
                      />
                      <span className="text-sm">
                        Li e aceito a{' '}
                        <button
                          onClick={() => setModalView('privacy')}
                          className="text-accent hover:underline"
                        >
                          Política de Privacidade da Integração
                        </button>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 justify-end pt-4" style={{ borderTop: '1px solid var(--cg-border)' }}>
                  <button
                    onClick={() => setShowTermsModal(false)}
                    className="btn h-10 px-6"
                    style={{ backgroundColor: 'var(--cg-bg-alt)' }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleAcceptTerms}
                    disabled={!termsAccepted || !privacyAccepted || loadingMeta}
                    className="btn btn-primary h-10 px-6"
                  >
                    {loadingMeta ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                        Ativando...
                      </>
                    ) : (
                      'Aceitar e Ativar'
                    )}
                  </button>
                </div>
              </div>
            )}

            {modalView === 'terms' && (
              <div className="p-6">
                <button
                  onClick={() => setModalView('intro')}
                  className="mb-4 text-accent hover:underline text-sm"
                >
                  ← Voltar
                </button>
                <MetaIntegrationTerms />
              </div>
            )}

            {modalView === 'privacy' && (
              <div className="p-6">
                <button
                  onClick={() => setModalView('intro')}
                  className="mb-4 text-accent hover:underline text-sm"
                >
                  ← Voltar
                </button>
                <MetaIntegrationPrivacy />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Erro Customizado */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={closeErrorModal}
        title={errorModal.title}
        message={errorModal.message}
        code={errorModal.code}
        suggestion={errorModal.suggestion}
        actionLabel={errorModal.actionLabel}
        onAction={errorModal.onAction}
      />
    </div>
  );
}
